import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Wallet, CalendarDays, Users, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { ThemePicker, themeLabel } from "@/components/ThemePicker";

const PROFILES = [
  { v: "solo", l: "Solo" },
  { v: "couple", l: "Couple" },
  { v: "famille", l: "Famille" },
];

const POPULAR = ["Paris", "Lyon", "Marseille", "Bordeaux", "Lille", "Nantes", "Toulouse", "Strasbourg"];

const Field = ({ icon: Icon, label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="flex items-center gap-2 text-sm font-semibold text-white mb-2">
      <Icon className="w-4 h-4 text-amber-400" /> {label}
    </span>
    {children}
  </label>
);

const inputCls = "w-full h-14 rounded-xl bg-white border-2 border-transparent px-4 text-[#0B131E] text-lg font-medium placeholder:text-gray-400 placeholder:font-normal focus:outline-none focus:border-amber-500 transition-colors";

export const SearchForm = () => {
  const nav = useNavigate();
  const [f, setF] = useState({ departure_city: "", budget: 400, duration: 2, travelers: 2, profile: "couple", theme: null });
  const cityRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const setProfile = (profile) => {
    const travelers = profile === "solo" ? 1 : profile === "couple" ? 2 : Math.max(3, Number(f.travelers));
    setF({ ...f, profile, travelers });
  };

  const submit = async (e, override = {}) => {
    e?.preventDefault();
    const q = { ...f, ...override };
    if (q.departure_city.trim().length < 2) return toast.error("Indiquez une ville de départ.");
    setLoading(true);
    try {
      const { data } = await http.post("/search", {
        ...q, budget: Number(q.budget), duration: Number(q.duration), travelers: Number(q.travelers),
      });
      nav(`/resultats/${data.search_id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Vérifiez votre budget (min. 50 €) et le nombre de voyageurs (1 à 10).");
      setLoading(false);
    }
  };

  const pickTheme = (theme) => {
    const next = f.theme === theme ? null : theme;
    setF({ ...f, theme: next });
    if (!next) return;
    if (f.departure_city.trim().length >= 2) return submit(null, { theme: next });
    toast(`Thème « ${themeLabel(next)} » choisi : indiquez votre ville de départ.`);
    cityRef.current?.focus();
  };

  const quickSearch = (city) => {
    setF({ ...f, departure_city: city });
    submit(null, { departure_city: city });
  };

  return (
    <form onSubmit={submit} data-testid="search-form" className="rounded-2xl bg-[#15202B]/90 backdrop-blur-xl border border-white/15 p-6 sm:p-8 shadow-2xl">
      <div className="grid gap-4 sm:gap-5 grid-cols-2 lg:grid-cols-4">
        <Field icon={MapPin} label="Ville de départ" className="col-span-2 lg:col-span-1">
          <input ref={cityRef} data-testid="search-city-input" className={inputCls} placeholder="Ex : Lyon, Nantes, Lille…" value={f.departure_city} onChange={set("departure_city")} />
        </Field>
        <Field icon={Wallet} label="Budget total (€)">
          <input data-testid="search-budget-input" type="number" min={50} max={10000} step={10} className={inputCls} value={f.budget} onChange={set("budget")} />
        </Field>
        <Field icon={CalendarDays} label="Durée" className="col-span-2 sm:col-span-1 order-last sm:order-none">
          <div className="grid grid-cols-3 gap-1.5 h-14 p-1.5 rounded-xl bg-white">
            {[1, 2, 3].map((d) => (
              <button type="button" key={d} data-testid={`search-duration-${d}`} onClick={() => setF({ ...f, duration: d })}
                className={`rounded-lg text-base font-semibold transition-colors ${Number(f.duration) === d ? "bg-[#0B131E] text-white" : "text-gray-700 hover:bg-gray-100"}`}>
                {d} jour{d > 1 ? "s" : ""}
              </button>
            ))}
          </div>
        </Field>
        <Field icon={Users} label="Voyageurs">
          <input data-testid="search-travelers-input" type="number" min={1} max={10} className={inputCls} value={f.travelers} onChange={set("travelers")} />
        </Field>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2" role="radiogroup">
          <span className="text-sm font-semibold text-white mr-1">Vous partez :</span>
          {PROFILES.map((p) => (
            <button type="button" key={p.v} data-testid={`search-profile-${p.v}`} onClick={() => setProfile(p.v)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${f.profile === p.v ? "bg-white text-[#0B131E] border-white" : "border-white/30 text-gray-200 hover:border-white/60"}`}>
              {p.l}
            </button>
          ))}
        </div>
        <button type="submit" data-testid="search-submit-btn" disabled={loading}
          className="group inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-amber-500 hover:bg-amber-400 text-[#0B131E] text-base font-bold transition-colors shadow-[0_8px_30px_rgba(245,158,11,0.4)]">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Trouver mon escapade
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
      <div data-testid="popular-cities" className="mt-6 pt-5 border-t border-white/10 flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-300 mr-1">Départs populaires :</span>
        {POPULAR.map((c) => (
          <button type="button" key={c} disabled={loading} onClick={() => quickSearch(c)}
            data-testid={`popular-city-${c.toLowerCase()}`}
            className="px-3.5 py-1.5 rounded-full text-sm text-gray-100 bg-white/5 border border-white/15 hover:bg-amber-500 hover:text-[#0B131E] hover:border-amber-500 disabled:opacity-50 transition-colors">
            {c}
          </button>
        ))}
      </div>
      <ThemePicker value={f.theme} onPick={pickTheme} disabled={loading} />
    </form>
  );
};
